import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export type SelectOption = {
  value: string;
  label: string;
  depth?: number;
  keywords?: string;
  hint?: string;
};

export default function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Choose…',
  searchPlaceholder = 'Search…',
  emptyText = 'Nothing matches.',
  disabled,
  testId,
  className,
}: {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  testId?: string;
  className?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const chosen = options.find((option) => option.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={`w-full justify-between font-normal ${chosen ? '' : 'text-muted-foreground'} ${className ?? ''}`}
          data-testid={testId}
        >
          <span className="truncate">{chosen ? chosen.label : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput
            placeholder={searchPlaceholder}
            data-testid={testId ? `${testId}-search` : undefined}
          />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value || '__none__'}
                  value={`${option.label} ${option.keywords ?? ''}`}
                  onSelect={() => { onChange(option.value); setOpen(false); }}
                  data-testid={testId ? `${testId}-option-${option.value || 'none'}` : undefined}
                >
                  <Check className={`mr-2 h-4 w-4 shrink-0 ${option.value === value ? 'opacity-100' : 'opacity-0'}`} />
                  <span style={{ paddingLeft: `${(option.depth ?? 0) * 14}px` }} className="truncate">
                    {(option.depth ?? 0) > 0 && (
                      <span className="mr-1 text-muted-foreground">└</span>
                    )}
                    {option.label}
                  </span>
                  {option.hint && (
                    <span className="ml-auto pl-2 text-xs text-muted-foreground">{option.hint}</span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
